import { gaRuntime, runtimeEnv } from '@/config/featureFlags';
import { buildAnalyticsPayload } from './analyticsPayload';
import { sanitizeAnalyticsParams } from './analyticsPrivacy';
import { validateAnalyticsEvent, assertAnalyticsParamBudget } from './analyticsValidation';
import { webGtagTransport } from './transports/webGtagTransport';
import { ANALYTICS_EVENTS } from './events';
import { countDeclaredParams, isGlobalsProfile, resolveGlobalsProfile } from './globalsProfile';

const transports = [];

function isEventDefinition(value) {
  return (
    value !== null &&
    typeof value === 'object' &&
    typeof value.name === 'string'
  );
}

function resolveEventName(eventDefinitionOrName) {
  if (isEventDefinition(eventDefinitionOrName)) {
    return eventDefinitionOrName.name;
  }
  if (typeof eventDefinitionOrName === 'string') {
    return eventDefinitionOrName;
  }
  return '';
}

/** 事件名 → 事件定义索引（惰性构建；仅用于把字符串调用回查到声明） */
let eventDefinitionByName = null;

function getEventDefinitionByName(name) {
  if (!eventDefinitionByName) {
    eventDefinitionByName = new Map();
    for (const definition of Object.values(ANALYTICS_EVENTS)) {
      if (definition && typeof definition.name === 'string') {
        eventDefinitionByName.set(definition.name, definition);
      }
    }
  }
  return eventDefinitionByName.get(name) || null;
}

/** 事件定义 / 事件名 → 事件定义（查不到返回 null） */
function resolveEventDefinition(eventDefinitionOrName) {
  if (isEventDefinition(eventDefinitionOrName)) {
    return eventDefinitionOrName;
  }
  if (typeof eventDefinitionOrName === 'string') {
    return getEventDefinitionByName(eventDefinitionOrName);
  }
  return null;
}

/**
 * 解析本次该用的全局字段档位（方案 §4.3 / §5 阶段 2）。
 *
 * - 传事件定义：直接用它的声明（requiredParams ∪ optionalParams）派生档位；
 *   定义上显式写了 `globalsProfile` 则以它为准（用于个别事件的例外）。
 * - 传字符串名：回查事件注册表（`src/extension/background/background.js` 用字符串调用）；
 *   查不到（未登记的事件名）则保守取 `full` —— 宁可多带字段，也不要默默少带。
 *
 * ⚠️ 档位只看**声明**，不看本次实际传了几条（§4.6 硬约束 1）。
 */

/**
 * 解析事件级「参数下沉」声明（阶段 3 / 方案 §3.6.2）。
 *
 * 返回 `{ constants, redundantParams }`，两者都可能为 `null`（该事件没有下沉声明）。
 * 与 `resolveEventGlobalsProfile` 一样，**字符串调用**也能回查到声明
 * （Chrome 扩展的 background 用字符串名调用）。
 */
export function resolveEventSunkParams(eventDefinitionOrName) {
  const definition = resolveEventDefinition(eventDefinitionOrName);
  return {
    constants: definition && definition.constants ? definition.constants : null,
    redundantParams: definition && definition.redundantParams ? definition.redundantParams : null
  };
}

export function resolveEventGlobalsProfile(eventDefinitionOrName) {
  const definition = resolveEventDefinition(eventDefinitionOrName);

  const override = definition ? definition.globalsProfile : undefined;
  if (override !== undefined && !isGlobalsProfile(override) && runtimeEnv.isDevelopment) {
    console.warn(
      `[Analytics] Invalid globalsProfile "${override}" on "${resolveEventName(eventDefinitionOrName)}"` +
        '（已回退为按声明自有参数个数派生，见 globalsProfile.js）'
    );
  }

  return resolveGlobalsProfile(countDeclaredParams(definition), override);
}

export function registerAnalyticsTransport(transport) {
  if (
    transport &&
    typeof transport.sendEvent === 'function'
  ) {
    transports.push(transport);
  }
}

export function trackEvent(eventDefinitionOrName, params = {}, options = {}) {
  const eventName = resolveEventName(eventDefinitionOrName);

  if (!eventName) {
    console.warn('[Analytics] Invalid event definition or name');
    return;
  }

  const shouldValidate = options.validate !== false;

  if (shouldValidate) {
    validateAnalyticsEvent(eventDefinitionOrName, params);
  }

  const sanitized = sanitizeAnalyticsParams(params);
  // ⚠️ 全局字段档位必须由**事件定义的声明**静态决定（阶段 2 / §4.3）：
  // 重事件（声明自有参数多）只带 timestamp + environment，把参数槽让给业务字段。
  // 注意这里不读 sanitized 的条数 —— 按运行时条数动态裁剪会破坏可分析性（§4.6 硬约束 1）。
  const globalsProfile = resolveEventGlobalsProfile(eventDefinitionOrName);
  // 阶段 3：事件级常量 / 恒等冗余下沉 —— 在 payload 层按声明强制剥离（防回归 + dev 失配告警）
  const { constants, redundantParams } = resolveEventSunkParams(eventDefinitionOrName);
  const payload = buildAnalyticsPayload(sanitized, { globalsProfile, constants, redundantParams });

  if (shouldValidate) {
    // 必须在 payload 产出之后判，因为全局字段也占用 GA4 的 36 个参数槽
    assertAnalyticsParamBudget(eventName, payload);
  }

  for (const transport of transports) {
    try {
      if (typeof transport.isAvailable === 'function' && !transport.isAvailable()) {
        continue;
      }
      transport.sendEvent(eventName, payload);
    } catch (error) {
      console.error('[Analytics] Transport send failed:', error);
    }
  }
}

export function setUserId(userId) {
  const measurementId = gaRuntime.measurementId;

  for (const transport of transports) {
    try {
      if (typeof transport.isAvailable === 'function' && !transport.isAvailable()) {
        continue;
      }
      if (typeof transport.setUserId === 'function') {
        transport.setUserId(measurementId, userId);
      }
    } catch (error) {
      console.error('[Analytics] Transport setUserId failed:', error);
    }
  }
}

registerAnalyticsTransport(webGtagTransport);
